import { animate, state, style, transition, trigger } from "@angular/animations";
import { isPlatformBrowser } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from "@angular/core";
import { SafeHtml } from "@angular/platform-browser";
import { ImageAlias } from "@core/enums/image-alias.enum";
import { ImageInterface, ImageRevisionInterface } from "@core/interfaces/image.interface";
import { ImageApiService } from "@core/services/api/classic/images/image/image-api.service";
import { AuthService } from "@core/services/auth.service";
import { DeviceService } from "@core/services/device.service";
import { ImageService } from "@core/services/image/image.service";
import { ImageViewerNavigationContext } from "@core/services/image-viewer.service";
import { TutorialService } from "@core/services/tutorial.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { TutorialFactoryService } from "@shared/components/misc/tutorial/tutorial-factory.service";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";

@Component({
  selector: "astrobin-image-viewer-additional-buttons",
  animations: [
    trigger("slideAnimation", [
      state(
        "collapsed",
        style({
          opacity: 0.95
        })
      ),
      state(
        "expanded",
        style({
          opacity: 1
        })
      ),
      transition("collapsed => expanded", [animate("150ms cubic-bezier(0.25, 0.8, 0.25, 1)")]),
      transition("expanded => collapsed", [animate("120ms cubic-bezier(0.25, 0.8, 0.25, 1)")])
    ]),
    trigger("fadeAnimation", [
      transition(":enter", [
        style({ opacity: 0, transform: "translateX(-10px)" }),
        animate("120ms cubic-bezier(0.25, 0.8, 0.25, 1)", style({ opacity: 1, transform: "translateX(0)" }))
      ]),
      transition(":leave", [
        style({ opacity: 1, transform: "translateX(0)" }),
        animate("100ms cubic-bezier(0.25, 0.8, 0.25, 1)", style({ opacity: 0, transform: "translateX(10px)" }))
      ])
    ])
  ],
  template: `
    <div class="buttons-wrapper">
      <!-- Always visible mouse hover button for touch devices -->
      <div *ngIf="isTouchOnly && hasMouseHover" class="p-1">
        <button
          [class.active]="forceViewMouseHover"
          (click)="toggleViewMouseHover.emit()"
          class="force-view-mousehover-button btn btn-link btn-no-block text-light"
          astrobinEventPreventDefault
        >
          <fa-icon
            [ngbTooltip]="shouldShowTooltips ? ('Show mouse hover panels' | translate) : null"
            [openDelay]="shouldShowTooltips ? tooltipOpenDelay : 0"
            container="body"
            icon="crosshairs"
          ></fa-icon>
        </button>
      </div>

      <!-- Standalone plate-solving annotations toggle when hover is disabled -->
      <div *ngIf="!isTouchOnly && hasMouseHover && !showAnnotationsOnMouseHover" class="p-1">
        <button
          [class.active]="forceViewMouseHover"
          (click)="toggleViewMouseHover.emit()"
          class="force-view-annotations-button btn btn-link btn-no-block text-light"
          astrobinEventPreventDefault
        >
          <fa-icon
            [ngbTooltip]="shouldShowTooltips ? ('Toggle plate-solving annotations' | translate) : null"
            [openDelay]="shouldShowTooltips ? tooltipOpenDelay : 0"
            container="body"
            icon="crosshairs"
          ></fa-icon>
        </button>
      </div>

      <div
        [class.collapsed]="isCollapsed"
        [class.hidden-during-resize]="isResizing"
        class="btn-container"
        [@slideAnimation]="isCollapsed ? 'collapsed' : 'expanded'"
      >
        <!-- Expanded vertical menu with labels -->
        <div *ngIf="!isCollapsed" class="expanded-menu" [@fadeAnimation]>
          <div *ngIf="!isTouchOnly && hasMouseHover" class="menu-item menu-toggle-item">
            <astrobin-toggle-button
              [label]="'Plate-solving annotations on hover' | translate"
              [value]="showAnnotationsOnMouseHover"
              (toggle)="toggleAnnotationsOnMouseHover.emit()"
              labelClassName="menu-toggle-label"
            ></astrobin-toggle-button>
          </div>

          <button
            *ngIf="revision?.solution?.pixscale"
            [class.active-moon]="moonOverlayActive"
            [class.text-light]="!moonOverlayActive"
            (click)="toggleMoonOverlay.emit(); closeMenu()"
            class="menu-item btn btn-link btn-no-block"
            astrobinEventPreventDefault
          >
            <fa-icon icon="moon"></fa-icon>
            <span class="label">{{ moonOverlayActive ? hideMoonScaleTooltip : showMoonScaleTooltip }}</span>
          </button>

          <button
            *ngIf="revision?.solution && (revision?.solution.skyplotZoom1 || revision?.solution.pixinsightFindingChart)"
            (click)="openSkyplot(); closeMenu()"
            class="menu-item btn btn-link btn-no-block text-light"
            astrobinEventPreventDefault
          >
            <fa-icon icon="map"></fa-icon>
            <span class="label">{{ "View sky map" | translate }}</span>
          </button>

          <button
            *ngIf="!revision?.videoFile"
            (click)="openHistogram(); closeMenu()"
            class="menu-item btn btn-link btn-no-block text-light"
          >
            <fa-icon icon="chart-simple"></fa-icon>
            <span class="label">{{ "View histogram" | translate }}</span>
          </button>

          <button
            *ngIf="!revision?.videoFile"
            [class.text-muted]="!hasAdvancedSolutionMatrix"
            [disabled]="!hasAdvancedSolutionMatrix"
            (click)="hasAdvancedSolutionMatrix && onToggleMeasurementTool($event); closeMenu()"
            class="menu-item btn btn-link btn-no-block text-light"
            astrobinEventPreventDefault
          >
            <fa-icon icon="ruler"></fa-icon>
            <span class="label">{{
              hasAdvancedSolutionMatrix
                ? ("Measure distances" | translate)
                : ("The measuring tool is only available on images solved with PixInsight." | translate)
            }}</span>
          </button>

          <button
            *ngIf="!revision?.videoFile"
            [class.active]="hasUrlAnnotations"
            [class.text-light]="true"
            (click)="onToggleAnnotations($event)"
            class="menu-item btn btn-link btn-no-block"
            astrobinEventPreventDefault
          >
            <fa-icon icon="file-text"></fa-icon>
            <span class="label">{{
              hasUrlAnnotations ? ("Annotations from URL available" | translate) : ("Annotate image" | translate)
            }}</span>
          </button>

          <button
            *ngIf="
              image.allowImageAdjustmentsWidget === true ||
              (image.allowImageAdjustmentsWidget === null && image.defaultAllowImageAdjustmentsWidget)
            "
            (click)="showAdjustmentsEditor.emit(); closeMenu()"
            class="menu-item btn btn-link btn-no-block text-light d-none d-md-flex"
            astrobinEventPreventDefault
            astrobinEventStopPropagation
          >
            <fa-icon icon="sliders"></fa-icon>
            <span class="label">{{ "Image adjustments" | translate }}</span>
          </button>
        </div>

        <!-- Always visible toggle button -->
        <button
          [class.expanded]="!isCollapsed"
          (click)="toggleCollapse($event)"
          class="toggle-buttons-btn kebab-menu btn-no-block"
          astrobinEventPreventDefault
          data-tutorial-id="image-tools-menu"
        >
          <fa-icon *ngIf="isCollapsed" icon="toolbox"></fa-icon>
          <fa-icon *ngIf="!isCollapsed" icon="times"></fa-icon>
        </button>
      </div>
    </div>

    <ng-template #skyplotModalTemplate>
      <div class="modal-body">
        <img
          [ngStyle]="{ filter: revision.solution.pixinsightFindingChart ? 'none' : 'grayscale(100%)' }"
          [src]="revision?.solution?.pixinsightFindingChart || revision?.solution?.skyplotZoom1"
          class="w-100"
          alt=""
        />
      </div>
    </ng-template>

    <ng-template #histogramModalTemplate>
      <div class="modal-body">
        <img *ngIf="!loadingHistogram; else loadingTemplate" [src]="histogram" alt="" />
      </div>
    </ng-template>

    <ng-template #loadingTemplate>
      <astrobin-loading-indicator></astrobin-loading-indicator>
    </ng-template>
  `,
  styleUrls: ["./image-viewer-additional-buttons.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageViewerAdditionalButtonComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
  /**
   * Tooltip open delay in ms for all additional toolbar tooltips.
   * Use this variable in the template for consistency.
   */
  readonly tooltipOpenDelay = 500;
  isCollapsed = true;

  @HostBinding("class.standalone-mode")
  get isStandaloneMode(): boolean {
    return this.standalone;
  }

  @Input() image: ImageInterface;
  @Input() revisionLabel: string;
  @Input() standalone: boolean = false;

  @Input() showAnnotationsOnMouseHover: boolean;
  @Input() hasMouseHover: boolean;
  @Input() inlineSvg: SafeHtml;
  @Input() forceViewMouseHover: boolean;
  @Input() forceViewAnnotationsMouseHover: boolean;
  @Input() moonOverlayActive = false;
  @Input() annotationsActive = false;
  @Input() annotationsReadOnly = true;
  @Input() hasUrlAnnotations = false;
  @Input() hasAdvancedSolutionMatrix = false;
  @Input() isResizing = false;
  @Input() isFullscreen = false;
  @Input() navigationContext: ImageViewerNavigationContext = null;

  // Only for desktops.
  @Output() toggleAnnotationsOnMouseHover = new EventEmitter<void>();

  // This is the one for mobile devices.
  @Output() toggleViewMouseHover = new EventEmitter<void>();

  // Toggle annotations overlay manually when hover is disabled
  @Output() toggleViewAnnotationsMouseHover = new EventEmitter<void>();

  @Output() showAdjustmentsEditor = new EventEmitter<void>();
  @Output() toggleMoonOverlay = new EventEmitter<void>();
  @Output() toggleMeasurementTool = new EventEmitter<MouseEvent>();
  @Output() toggleAnnotations = new EventEmitter<MouseEvent>();
  @Output() toggleAnnotationEditMode = new EventEmitter<void>();

  @ViewChild("skyplotModalTemplate")
  skyplotModalTemplate: TemplateRef<any>;

  @ViewChild("histogramModalTemplate")
  histogramModalTemplate: TemplateRef<any>;

  protected readonly isBrowser: boolean;
  protected readonly isTouchOnly: boolean;

  protected revision: ImageInterface | ImageRevisionInterface;
  protected loadingHistogram = false;
  protected histogram: string;
  protected showMoonScaleTooltip: string;
  protected hideMoonScaleTooltip: string;
  // Protected variable to check if we should show tooltips
  protected readonly shouldShowTooltips: boolean;
  // Collapsible state
  private _hoverTimeout: any;
  private _touchTimeout: any;
  // Track the tutorial status for cleanup
  private _tutorialActive = false;
  private _tutorialCheckSubscription: Subscription;

  constructor(
    public readonly modalService: NgbModal,
    public readonly imageApiService: ImageApiService,
    public readonly imageService: ImageService,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly translateService: TranslateService,
    public readonly deviceService: DeviceService,
    public readonly utilsService: UtilsService,
    public readonly tutorialService: TutorialService,
    public readonly tutorialFactoryService: TutorialFactoryService,
    public readonly elementRef: ElementRef,
    public readonly authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isTouchOnly = this.deviceService.isTouchEnabled() && !this.deviceService.isHybridPC();

    // Initialize shouldShowTooltips based on device type
    this.shouldShowTooltips = !this.deviceService.isTouchEnabled();

    // Initialize tooltip texts for i18n extraction
    this.showMoonScaleTooltip = this.translateService.instant("Show Moon scale (M)");
    this.hideMoonScaleTooltip = this.translateService.instant("Hide Moon scale (M)");
  }

  ngOnInit(): void {
    // Start collapsed
    this.isCollapsed = true;
  }

  ngAfterViewInit(): void {
    // Check if we need to show the tutorial
    if (this.isBrowser) {
      // Use DOM detection to identify if we're in a fullscreen view
      // The fullscreen viewer has class 'fullscreen-image-viewer' on a parent element
      const isInFullscreenViewer = !!document.querySelector(".fullscreen-image-viewer");

      // Skip tutorial if we're in fullscreen mode or other tools are active
      if (
        isInFullscreenViewer ||
        this.annotationsActive ||
        this.hasUrlAnnotations ||
        this.moonOverlayActive ||
        this.forceViewMouseHover
      ) {
        return;
      }

      // First check if user is authenticated
      this.authService
        .isAuthenticated$()
        .pipe(take(1))
        .subscribe(isAuthenticated => {
          // Only show tutorial for authenticated users
          if (!isAuthenticated) {
            return;
          }

          // Import tutorial constants here to avoid circular dependencies
          void import("@shared/constants/tutorial.constants").then(({ TUTORIAL_IDS }) => {
            // Check if another tutorial is already active
            this._tutorialCheckSubscription = this.tutorialService.activeTutorial$.subscribe(activeTutorial => {
              // Don't show image tools tutorial if:
              // 1. It has already been completed
              // 2. Another tutorial is already active
              // 3. We're in an annotation-related mode
              if (
                !this.tutorialService.hasTutorialBeenCompleted(TUTORIAL_IDS.IMAGE_TOOLS) &&
                !activeTutorial &&
                !this.annotationsActive
              ) {
                // Additional safety check - don't proceed if other tutorials are about to start
                this.tutorialService.activeTutorial$.pipe(take(1)).subscribe(freshCheck => {
                  if (!freshCheck) {
                    const translatedTutorial = this.tutorialFactoryService.createTutorial("IMAGE_TOOLS");
                    this.tutorialService.startTutorial(translatedTutorial);
                    this._tutorialActive = true;
                    this.changeDetectorRef.markForCheck();
                  }
                });
              }

              // Always unsubscribe after checking once, but only if it exists
              if (this._tutorialCheckSubscription) {
                this._tutorialCheckSubscription.unsubscribe();
                this._tutorialCheckSubscription = null;
              }
            });
          });
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.image || changes.revisionLabel) {
      this.revision = this.imageService.getRevision(this.image, this.revisionLabel);

      // Reset moon overlay active state when image changes
      if (changes.image && changes.image.previousValue !== changes.image.currentValue) {
        this.moonOverlayActive = false;
      }
    }
  }

  openSkyplot(): void {
    this.modalService.open(this.skyplotModalTemplate, { size: "md" });
  }

  openHistogram(): void {
    if (this.loadingHistogram) {
      return;
    }

    this.modalService.open(this.histogramModalTemplate, { size: "sm" });
    this.loadingHistogram = true;

    this.imageApiService
      .getThumbnail(this.image.hash || this.image.pk, this.revisionLabel, ImageAlias.HISTOGRAM)
      .subscribe(thumbnail => {
        this.loadingHistogram = false;
        this.histogram = thumbnail.url;
        this.changeDetectorRef.markForCheck();
      });
  }

  /**
   * Toggle tools visibility when clicking the chevron or close button
   * with enhanced animation handling
   */
  toggleCollapse(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Toggle collapsed state
    this.isCollapsed = !this.isCollapsed;

    // Apply additional classes for animation state
    if (this.isBrowser) {
      const container = this.elementRef.nativeElement.querySelector(".btn-container");
      if (container) {
        // Force a reflow to ensure animations work properly
        void container.offsetWidth;
      }
    }
  }

  closeMenu(): void {
    this.isCollapsed = true;
    this.changeDetectorRef.markForCheck();
  }

  private clearTimeouts(): void {
    if (this._hoverTimeout) {
      clearTimeout(this._hoverTimeout);
      this._hoverTimeout = null;
    }

    if (this._touchTimeout) {
      clearTimeout(this._touchTimeout);
      this._touchTimeout = null;
    }
  }

  /**
   * Cleanup any timeouts when component is destroyed
   */
  ngOnDestroy(): void {
    if (this._hoverTimeout) {
      clearTimeout(this._hoverTimeout);
    }

    if (this._touchTimeout) {
      clearTimeout(this._touchTimeout);
    }

    // Clean up tutorial check subscription if it exists
    if (this._tutorialCheckSubscription) {
      this._tutorialCheckSubscription.unsubscribe();
      this._tutorialCheckSubscription = null;
    }

    // If we're being destroyed with an active tutorial, end it
    if (this._tutorialActive) {
      this.tutorialService.endTutorial(false);
    }
  }

  onToggleMeasurementTool(event: MouseEvent): void {
    // If we have an active tutorial, end it before enabling measurement tool
    if (this._tutorialActive) {
      this.tutorialService.endTutorial(true);
      this._tutorialActive = false;
    }

    // Then emit the event to activate measurement tool
    this.toggleMeasurementTool.emit(event);
  }

  onToggleAnnotations(event: MouseEvent): void {
    // If we have an active tutorial, end it before enabling annotations
    if (this._tutorialActive) {
      this.tutorialService.endTutorial(true);
      this._tutorialActive = false;
    }

    // Then emit the event to activate annotations
    this.toggleAnnotations.emit(event);
  }
}
